import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { connect } from 'redux-bundler-react';

import { isValidText, sanitizeText } from '../../../utils/helpers';
import { ErrorMessages } from '@src/utils/enums';
import genericSecondaryModal from '../../../app-pages/Forms/components/modals/genericSecondaryModal';
import { useModal } from '@stores';

const debounce = (func, wait) => {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const ExplanationTableCell = connect(({ getValue, row, column, table, cell }) => {
  const { openSecondaryModal } = useModal();
  const columnMeta = column.columnDef.meta;
  const tableMeta = table.options.meta;
  const initialValue = getValue();
  const [value, setValue] = useState(initialValue);
  const [approvalType, setApprovalType] = useState();
  const rowApprovalType = useMemo(() => row.getValue('approvalType'), [row]);
  const debouncedUpdateRef = useRef();

  useEffect(() => {
    debouncedUpdateRef.current = debounce((newValue) => {
      if (tableMeta?.updateData) {
        tableMeta?.updateData(
          row.index,
          column.id,
          columnMeta?.type === 'number' ? Number(newValue) : (newValue ?? newValue)
        );
      }
    }, 500);
  }, [row.index, column.id, tableMeta?.updateData, columnMeta?.type, tableMeta]);

  const updateValue = useCallback((newValue) => {
    debouncedUpdateRef.current(newValue);
  }, []);

  useEffect(() => {
    rowApprovalType && setApprovalType(rowApprovalType);
  }, [rowApprovalType]);

  // Clear explanation field if approval type is not 'Other'
  useEffect(() => {
    if (approvalType !== 'Other' && approvalType !== undefined) {
      setValue(null);
      updateValue(null);
    }
  }, [approvalType, setValue, updateValue]);

  const handleChange = (e) => {
    if (approvalType === 'Other') {
      setValue(e?.target?.value);
      updateValue(e?.target?.value);
    }
  };

  // Handling special characters
  useEffect(() => {
    if (value && typeof value === 'string') {
      if (isValidText(value)) {
        setValue(value);
        updateValue(value);
      } else {
        // Optionally handle invalid input here, such as warning the user
        const msg = ErrorMessages.InvalidCharacters;
        openSecondaryModal(genericSecondaryModal, { title: 'Invalid Characters', msg: msg });
        setValue(sanitizeText(value));
        updateValue(sanitizeText(value));
      }
    }
  }, [value, setValue, updateValue, openSecondaryModal]);

  return (
    <input
      aria-label={'Explanation'}
      disabled={approvalType !== 'Other' || columnMeta?.readOnly}
      id={cell.id}
      maxLength={4000}
      onChange={handleChange}
      placeholder={approvalType !== 'Other' ? 'N/A' : ''}
      required={columnMeta?.required}
      style={{
        width: '100%',
        borderColor: 'hsl(0, 0%, 80%)',
        cursor: columnMeta?.readOnly || approvalType !== 'Other' ? 'not-allowed' : 'auto',
      }}
      type='text'
      value={value ?? ''}
    />
  );
});

export default ExplanationTableCell;
